/**
 * Visa / Foreign-Settlement Yoga v2 — six-signal classical engine.
 *
 * Two layers:
 *   - Layer 1: plain-language takeaways (4-5 bullets, score in headline)
 *   - Layer 2: five SignalCards (Rahu, 12th-house, 9th-lord, karakamsa,
 *     dasha) + a dedicated Direction block with weighted-contributor
 *     breakdown
 *
 * Includes Hindi/English toggle and PDF download — same pattern as
 * the karmic page.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  ChartCreateRequest,
  VisaSignalEntry,
  VisaYogaResponse,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function VisaYogaPage({ onBack }: { onBack: () => void }) {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [submittedReq, setSubmittedReq] = useState<ChartCreateRequest | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const reportMut = useMutation<VisaYogaResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.visaYoga(req),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      setSubmittedReq(req);
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
      if (e.status === 401) return t('फिर से प्रवेश करें', 'Please sign in again');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  const downloadPdf = useCallback(async () => {
    if (!submittedReq) return;
    try {
      setPdfDownloading(true);
      const blob = await api.visaYogaPdfBlob(submittedReq, lang);
      const name = (report?.input.name ?? 'visa').replace(/\s+/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visa_yoga_${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [submittedReq, lang, report, t]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <button
        type="button"
        onClick={onBack}
        className="self-start text-xs text-saffron-300 hover:underline"
      >
        ← {t('मेन्यू पर वापस', 'Back to menu')}
      </button>

      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            {t('विदेश योग — विदेश-बसाव विश्लेषण', 'Visa Yoga — Foreign-Settlement Analysis')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              'छह शास्त्रीय संकेतों के आधार पर — राहु, द्वादश भाव, नवमेश, कारकांश, दशा-काल, दिशा।',
              'Based on six classical signals — Rahu, 12th house, 9th lord, Karakamsa, dasha period, direction.',
            )}
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
          <button
            type="button"
            onClick={() => setLang('hi')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'hi'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'hi'}
          >
            हिन्दी
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'en'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'en'}
          >
            English
          </button>
        </div>
      </header>

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('विश्लेषण हो रहा है…', 'Analysing…')}
        </p>
      )}

      {report && (
        <ReportView
          report={report}
          lang={lang}
          t={t}
          onDownloadPdf={downloadPdf}
          pdfDownloading={pdfDownloading}
        />
      )}
    </div>
  );
}

function ReportView({
  report,
  lang,
  t,
  onDownloadPdf,
  pdfDownloading,
}: {
  report: VisaYogaResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
}) {
  return (
    <article className="space-y-4">
      {/* Header card with score + PDF download */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-saffron-900/50 to-stone-900 p-5 ring-2 ring-saffron-700/50">
        <div>
          <p className="text-xs uppercase tracking-wider text-saffron-300">
            {t('विदेश योग रिपोर्ट', 'Visa Yoga Report')}
          </p>
          <p className="mt-1 text-xl font-semibold text-saffron-100">
            {report.input.name}
          </p>
          <p className="mt-0.5 text-xs text-stone-400">
            {report.input.dob} · {report.input.tob} ·{' '}
            {lang === 'hi' ? report.lagna.rashi_name_hi : report.lagna.rashi_name_en}{' '}
            {t('लग्न', 'Ascendant')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-saffron-300">
              {t('स्कोर', 'Score')}
            </p>
            <p className="text-3xl font-bold text-saffron-100">
              {report.foreign_pull_score}
              <span className="text-base text-stone-400">/100</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onDownloadPdf}
            disabled={pdfDownloading}
            className="rounded-xl bg-saffron-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
          >
            {pdfDownloading
              ? t('बन रहा है…', 'Generating…')
              : t('PDF डाउनलोड', 'Download PDF')}
          </button>
        </div>
      </section>

      {/* Layer 1 — Takeaways */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('मुख्य निष्कर्ष', 'Key Takeaways')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t('सरल भाषा में — ज्योतिषीय शब्दों के बिना', 'In plain language — no astrological jargon')}
        </p>
        <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
          {(lang === 'hi' ? report.takeaways_hi : report.takeaways_en).map((tk, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-saffron-400">●</span>
              <span>{tk}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Layer 2 — Detailed signal-by-signal */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('विस्तृत विश्लेषण', 'Detailed Analysis')}
        </h2>
        <p className="mb-4 text-xs text-stone-500">
          {t(
            'छह शास्त्रीय संकेत — स्रोत मेथडोलॉजी अनुभाग में',
            'Six classical signals — sources cited in the methodology footer',
          )}
        </p>
        <div className="space-y-3">
          <SignalCard line={report.rahu_signature} lang={lang} />
          <SignalCard line={report.twelfth_signature} lang={lang} />
          <SignalCard line={report.ninth_lord_signature} lang={lang} />
          <SignalCard line={report.karakamsa_signature} lang={lang} />
          <SignalCard line={report.dasha_signature} lang={lang} />
        </div>
      </section>

      {/* Direction block (separate, marked indicative) */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-base font-semibold text-saffron-200">
            {t('संभावित दिशा', 'Likely Direction')}
          </h2>
          <ConfidenceBadge confidence={report.direction.confidence} t={t} />
        </div>
        <div className="rounded-md border border-stone-700/60 bg-stone-900/40 p-4 text-center">
          <p className="text-3xl font-semibold text-saffron-100">
            {lang === 'hi' ? report.direction.direction_hi : report.direction.direction_en}
          </p>
        </div>
        <div className="mt-3">
          <p className="mb-1 text-[10px] uppercase tracking-wider text-stone-500">
            {t('योगदान', 'Contributors')}
          </p>
          <ul className="space-y-1 text-xs text-stone-300">
            {(lang === 'hi'
              ? report.direction.contributors_hi
              : report.direction.contributors_en
            ).map((c, i) => <li key={i}>· {c}</li>)}
          </ul>
        </div>
        <p
          className="mt-3 border-t border-stone-800/60 pt-2 text-[11px] italic leading-relaxed text-amber-300/80"
          dangerouslySetInnerHTML={{
            __html: (lang === 'hi'
              ? report.direction.indicative_only_hi
              : report.direction.indicative_only_en
            ).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>'),
          }}
        />
      </section>

      {/* Dasha timeline (active + upcoming foreign-favouring) */}
      {report.dasha_windows.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h2 className="mb-3 text-base font-semibold text-saffron-200">
            {t('दशा-कालक्रम', 'Dasha Timeline')}
          </h2>
          <ul className="space-y-2">
            {report.dasha_windows.map((w, i) => (
              <li
                key={i}
                className={
                  'rounded-md border-l-2 px-3 py-2 ' + (w.is_active
                    ? 'border-saffron-500 bg-saffron-900/20'
                    : 'border-stone-700 bg-stone-900/40')
                }
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-medium text-saffron-200">
                    {lang === 'hi' ? w.lord_hi : w.lord_en}
                    {w.is_active && (
                      <span className="ml-2 rounded-full bg-saffron-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-950">
                        {t('सक्रिय', 'Active')}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-stone-400">
                    {w.start.slice(0, 7)} → {w.end.slice(0, 7)}
                  </p>
                </div>
                <p className="mt-1 text-xs text-stone-300">
                  {lang === 'hi' ? w.reason_hi : w.reason_en}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Methodology — financial-disclaimer tier */}
      <section className="rounded-2xl bg-stone-900/20 p-3 ring-1 ring-stone-800/40">
        <h3 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
          {t('प्रयुक्त ज्योतिष-विधि', 'Methodology Used')}
        </h3>
        <ul className="space-y-0.5 text-[8.5px] leading-snug text-stone-500">
          {report.methods.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">
                {lang === 'hi' ? m.method_hi : m.method_en}
              </span>
              <span className="text-stone-600">
                {' — '}
                {lang === 'hi' ? m.source_hi : m.source_en}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 border-t border-stone-800/40 pt-1.5 text-[8.5px] italic leading-snug text-stone-600">
          {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
        </p>
        <p className="mt-1 text-[8px] text-stone-700">{report.rules_version}</p>
      </section>
    </article>
  );
}

function SignalCard({
  line,
  lang,
}: {
  line: VisaSignalEntry;
  lang: 'hi' | 'en';
}) {
  return (
    <div className="rounded-md border-l-2 border-saffron-700/60 bg-stone-900/40 px-3 py-2.5">
      <p className="text-sm font-medium text-saffron-300">
        {lang === 'hi' ? line.label_hi : line.label_en}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-stone-100">
        {lang === 'hi' ? line.text_hi : line.text_en}
      </p>
    </div>
  );
}

function ConfidenceBadge({
  confidence,
  t,
}: {
  confidence: 'high' | 'medium' | 'low';
  t: (hi: string, en: string) => string;
}) {
  const labelMap = {
    high: t('उच्च विश्वास', 'High confidence'),
    medium: t('मध्यम विश्वास', 'Medium confidence'),
    low: t('न्यून विश्वास', 'Low confidence'),
  };
  const colourMap = {
    high: 'bg-emerald-900/40 text-emerald-300 ring-emerald-700/50',
    medium: 'bg-amber-900/40 text-amber-300 ring-amber-700/50',
    low: 'bg-stone-800 text-stone-400 ring-stone-700',
  };
  return (
    <span
      className={
        'rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 '
        + colourMap[confidence]
      }
    >
      {labelMap[confidence]}
    </span>
  );
}
